import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  AfterViewInit,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { myCustomTheme } from '../../../constants/grid-themes';
import { MeetingNote } from '@models/meeting-notes.model';
import { MeetingNotesActions } from '@store/actions/meeting-notes/meeting-notes.actions';
import {
  selectMeetingNotes,
  selectMeetingNotesLoading,
} from '@store/selectors/meeting-notes/meeting-notes.selectors';
import {
  selectCanUploadMeetingNotes,
} from '@store/selectors/user/user.selectors';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';
import { MeetingNotesUploadModalComponent } from '../../modals/meeting-notes-upload-modal/meeting-notes-upload-modal.component';
import { MeetingNotesActionsRendererComponent } from './meeting-notes-actions-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-meeting-notes',
  standalone: true,
  imports: [CommonModule, AgGridAngular, MatButtonModule, MatIconModule],
  templateUrl: './meeting-notes.component.html',
  styleUrl: './meeting-notes.component.css',
})
export class MeetingNotesComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  theme = myCustomTheme;
  private gridApi: any;
  notes: MeetingNote[] = [];
  isLoading = true;

  rowData$ = this.store.select(selectMeetingNotes);
  loading$ = this.store.select(selectMeetingNotesLoading);
  canUpload$ = this.store.select(selectCanUploadMeetingNotes);

  private canUpload = false;

  public gridOptions: GridOptions = {
    rowSelection: 'single',
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    loading: true,
    rowData: [],
    onGridReady: this.onGridReady.bind(this),
  };

  colDefs: ColDef[] = [
    {
      field: 'meetingDescription',
      headerName: 'Meeting Description',
      flex: 1,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: 'meetingDate',
      headerName: 'Meeting Date',
      flex: 1,
      valueFormatter: (p: any) => {
        if (!p.value) return '';
        const [year, month, day] = (p.value as string).split('T')[0].split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      },
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      valueFormatter: (p: any) => p.value || 'Meeting Notes',
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      headerName: 'Actions',
      minWidth: 180,
      width: 180,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: MeetingNotesActionsRendererComponent,
    },
  ];

  defaultColDef: ColDef = {
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 100,
  };

  ngOnInit(): void {
    this.canUpload$.pipe(takeUntil(this.destroy$)).subscribe((can) => {
      this.canUpload = can;
    });

    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
      if (this.gridApi) {
        this.gridApi.setGridOption('loading', loading);
      }
    });

    this.rowData$.pipe(takeUntil(this.destroy$)).subscribe((notes) => {
      this.notes = notes || [];
      if (this.gridApi && !this.isLoading) {
        this.gridApi.setGridOption('rowData', this.notes);
      }
    });

    this.store.dispatch(MeetingNotesActions.loadMeetingNotes());
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('loading', this.isLoading);

    if (this.notes.length > 0) {
      this.gridApi.setGridOption('rowData', this.notes);
    }
  }

  onCellClicked(params: any): void {
    if (params.column.colId !== 'Actions' && params.column.colDef?.headerName !== 'Actions') return;

    const action = params.event?.target?.dataset?.action;
    if (!action) return;

    const data = params.data as MeetingNote;

    if (action === 'view') {
      this.store.dispatch(MeetingNotesActions.viewMeetingNote({ id: data.id, fileName: data.fileName }));
    } else if (action === 'download') {
      this.store.dispatch(MeetingNotesActions.downloadMeetingNote({ id: data.id, fileName: data.fileName }));
    } else if (action === 'delete' && this.canUpload) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        data: {
          title: 'Delete Meeting Note',
          content: `Are you sure you want to delete "${data.meetingDescription}"?`,
          confirmText: 'Delete',
          cancelText: 'Cancel',
        },
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(MeetingNotesActions.deleteMeetingNote({ id: data.id }));
        }
      });
    }
  }

  openUploadModal(): void {
    this.dialog.open(MeetingNotesUploadModalComponent, {
      width: '520px',
      disableClose: true,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
